/**
 * Color
 */
export class Color {
  primary: string = '#000000'; // will come from the portal config
  primary_10: string = '#000000'; // will come from the portal config
  primary_70: string = '#000000'; // will come from the portal config
  secondary: string = '#000000'; // will come from the portal config
  secondary_10: string = '#000000'; // will come from the portal config
  secondary_70: string = '#000000'; // will come from the portal config
  readonly black: string = '#000000';
  readonly white: string = '#FFFFFF';
  readonly neutral = {
    grey_50: '#F9FAFB',
    grey_100: '#F3F4F6',
    grey_200: '#E5E7EB',
    grey_300: '#D1D5DB',
    grey_500: '#6B7280',
    grey_600: '#4B5563',
    grey_800: '#1F2937',
    grey_900: '#111827',
  };
  readonly surface = {
    niv1: '#F9FAFB',
    white: this.white,
    background: '#F3F4F6',
  };
}
const color = new Color();

/**
 * Border
 */
export const border = {
  radius: {
    md: '16px',
    sm: '14px',
    xs: '12px',
    xxs: '8px',
  },
  boxShadow: '2px 2px 8px rgba(3, 7, 18, 0.16)',
} as const;
export type Border = typeof border;

/**
 * Box Shadow
 */
export class BoxShadow {
  readonly primary: string = 'rgba(3, 7, 18, 0.16) 2px 2px 8px 0px';
  readonly secondary: string = 'rgba(3, 7, 18, 0.25) 2px 2px 8px 0px';
}
const boxShadow = new BoxShadow();

/**
 * Margin
 */
export class Margin {
  readonly desktop = {
    xl: '32px',
    lg: '24px',
    md: '16px',
    sm: '8px',
  };
  readonly mobile = {
    xl: '16px',
    lg: '12px',
    md: '8px',
  };
}
const margin = new Margin();

/**
 * Padding
 */
export class Padding {
  readonly desktop = {
    xl: '32px',
    lg: '24px',
    md: '16px',
    sm: '12px',
    xs: '8px',
    xxs: '6px',
    xxxs: '4px',
  };
  readonly mobile = {
    xl: '32px',
    lg: '24px',
    md: '16px',
    sm: '12px',
    xs: '8px',
    xxs: '6px',
    xxxs: '4px',
  };
}
const padding = new Padding();

/**
 * Gap
 */
export class Gap {
  readonly desktop = {
    xxl: '32px',
    xl: '24px',
    lg: '16px',
    md: '12px',
    sm: '8px',
    xs: '6px',
    xxs: '4px',
  };
  readonly mobile = {
    xxl: '32px',
    xl: '24px',
    lg: '16px',
    md: '12px',
    sm: '8px',
    xs: '6px',
    xxs: '4px',
  };
}
export const gap = new Gap();

/**
 * Font size
 */
export class FontSize {
  readonly xxl: string = '2.5rem'; // 40px
  readonly xl: string = '2rem'; // 32px
  readonly lg: string = '1.25rem'; // 20px
  readonly md: string = '1rem'; // 16px
  readonly sm: string = '0.875rem'; // 14px
}
const fontSize = new FontSize();

/**
 * Font weight
 */
export class FontWeight {
  readonly bold: string = '700';
  readonly normal: string = '400';
}
const fontWeight = new FontWeight();

/**
 * Screen breakpoints
 */
export const screenBreakpoint = {
  md: {px: '820px', value: 820},
};
export type ScreenBreakpoint = typeof screenBreakpoint;

/**
 * Icon
 */
export const icon = {
  size: {lg: '48px', md: '24px'},
};
export type Icon = typeof icon;

/**
 * Skeleton
 */
export const skeleton = {
  text: {
    width: {
      xsm: '20px',
      sm: '40px',
      md: '80px',
      lg: '120px',
      xl: '160px',
      xxl: '200px',
    },
  },
};
export type Skeleton = typeof skeleton;

export const defaultTheme = {
  border: border,
  boxShadow: boxShadow,
  color: color,
  margin: margin,
  padding: padding,
  gap: gap,
  typography: {
    fontSize: fontSize,
    fontWeight: fontWeight,
  },
  screenBreakpoint: screenBreakpoint,
  icon: icon,
  skeleton: skeleton,
};
